/**
 * AppSettings document — the singleton `app-settings` doc in CosmosDB.
 *
 * Holds global configuration: which prompts/providers are active, named
 * pipeline configs, rate limits, tracing settings.
 *
 * Pipeline shape types are in `pipelineTypes.ts`.
 * Provider config doc shape is in `providerConfigTypes.ts`.
 */

import type { OperationType } from './operationTypes';
import type { TracingSettings, WebTracingSettings } from './tracing';
import type { OperationsMap } from './pipelineTypes';
import type { HintLevel } from './hintTypes';

/** Per-user-type rate limit thresholds */
export interface RateLimitTier {
    apiPerMinute: number;
    submissionsPerHour: number;
    llmPerDay: number;
}

/** Rate limit configuration stored in AppSettings */
export interface RateLimitConfig {
    student: RateLimitTier;
    teacher: RateLimitTier;
    admin: RateLimitTier;
}

export interface AppSettings {
    id: string;
    /**
     * Global default cap on hints per question that a student can reveal.
     * Governs both pre-written `Question.hints` AND hints generated at runtime by the LLM.
     * Individual questions can override this via `Question.maxHintsForStudent`.
     */
    maxHintsForStudent?: number;
    /**
     * Global default cap on how DIRECT a runtime LLM hint can get — the ceiling
     * that hint auto-escalation climbs toward. `nudge` < `direction` < `guided` <
     * `reveal`. Unset ⇒ `'guided'` (the historical hardcoded cap). A teacher's
     * per-subject `maxHintLevel` overrides this for their students.
     * See docs/FeatureDesigns/HintSystem/teacher-hint-level-control.md.
     */
    maxHintLevelAllowed?: HintLevel;
    // Which prompts are active (only these appear in pipeline config dropdowns)
    activePromptIds?: string[];
    // Structured per-operation config (legacy — use pipelineConfigs instead)
    operations?: OperationsMap;
    // Named pipeline configurations (each is a full set of operation configs)
    pipelineConfigs?: Record<string, OperationsMap>;
    // Which pipeline config to use when user has no override (defaults to 'default')
    defaultPipelineConfig?: string;
    /** Suffix selecting a variant of every provider tiered set, so the whole
     *  estate can be switched between model generations with one setting.
     *
     *  `'25'` resolves `analysis-tiered` → `analysis-tiered-25` and so on,
     *  falling back to the unsuffixed set wherever no twin exists. Unset ⇒ the
     *  unsuffixed sets, which is the default generation.
     *
     *  Chosen over duplicating whole pipeline configs: only the model choice
     *  varies between generations, and it lives entirely in the tiered sets —
     *  copying a config would duplicate ~35 cells, its prompts and its tier
     *  pins purely to vary 7 ladders, and the copies then drift.
     *  See docs/FeatureDesigns/PipelineInfra/llm-cost-attribution.md. */
    providerSetVariant?: string;
    /** Subscription id assigned to a guest when the `guest-subscription` experiment
     *  doesn't resolve a variant (experiment off / ring not in rollout). Unset ⇒
     *  guest gets no subscription (analysis_quota default '0' → immediate signup
     *  gate). See docs/FeatureDesigns/guest-subscription-experiment.md. */
    defaultGuestSubscriptionId?: string;
    /** The code students renew Plus/Pro with while payments are closed — named
     *  on the Your plan page, the welcome sheet and the renewal reminders. Unset
     *  ⇒ nothing names a code. See docs/FeatureDesigns/billing (target §9). */
    renewalCodeHint?: string;
    /** Pipeline config used for re-evaluations (student/teacher second look).
     *  Point this at frontier-tier cells so a re-eval runs a stronger model
     *  than the original analysis. Unset → re-evals use the user's normal
     *  config. See docs/FeatureDesigns/solution-analysis/student-re-evaluation.md. */
    reEvalPipelineConfig?: string;
    /** Pipeline config whose `evalJudge` operation runs the scenario-eval
     *  LLM-as-judge. Kept independent of the model-under-test so judging stays
     *  consistent across compared runs. Defaults to `defaultPipelineConfig`
     *  (else 'default'). See docs/FeatureDesigns/Evals/scenario-evals.md. */
    evalJudgeConfig?: string;
    /** How many low/medium-confidence answer dimensions the judge scores per
     *  LLM call. Defaults to 5. */
    evalJudgeBatchSize?: number;
    /** `splitConfidence` below which an auto-accepted page→question attribution is
     *  called doubtful, flagging the attempt and nudging the student to check it.
     *  Config rather than a literal because the right number depends on real
     *  distributions: too low and students never hear about a genuine mix-up, too
     *  high and they learn to ignore the warning. Defaults to 0.6.
     *  See docs/FeatureDesigns/assignments-tests/assignment-solution-upload.md. */
    solutionAttributionConfidenceThreshold?: number;
    /** How many extracted questions the `enrich_questions` pass covers per LLM
     *  call. Defaults to 5. Raising it cuts calls (and cost) but the model has
     *  to solve more questions in one response, which thins the later ones —
     *  the reason this is tunable rather than fixed.
     *  See docs/FeatureDesigns/assignments-tests/teacher-assignment-from-pdf.md. */
    enrichQuestionsBatchSize?: number;
    /** Which solution styles the enrichment pass authors per question.
     *  Defaults to `['formal']` — one solution, the benchmark answer-sheet one.
     *  Solutions are the expensive half of the output, so this is the main cost
     *  dial: `[]` skips them entirely (keeping answers + hints, which is what
     *  the marker and hint system need), and the full
     *  `['detailed','formal','quick']` is available when quality matters more
     *  than tokens. The output schema is built to match, so the model is
     *  constrained to exactly these rather than asked for three and trimmed. */
    enrichQuestionsSolutionStyles?: ('detailed' | 'formal' | 'quick')[];
    /** Check a teacher's answer key at import: the enrichment pass works out
     *  each key-answered question's answer itself, with the key hidden, and a
     *  question whose answer differs from the key's is flagged "Check key"
     *  (`keyReview.keyDisputed`). The key's answer is always kept. Absent ⇒ on.
     *  Off, key-answered questions are only classified, on the light set.
     *  See docs/FeatureDesigns/assignments-tests/paper-answer-key-target.md § Checking the key. */
    enrichQuestionsKeyCheck?: boolean;
    /** Classify imported questions (topic + difficulty) in a call of their own,
     *  one per subject, and leave the topic list out of the enrichment calls
     *  that solve them. Absent ⇒ off: enrichment classifies, as before. Needs a
     *  `classifyQuestions` pipeline in the active config; without one the job
     *  logs it and classifies in enrichment.
     *  See docs/FeatureDesigns/assignments-tests/paper-import-classification-target.md. */
    classifyQuestionsSeparately?: boolean;
    /** Questions per classify call. Defaults to 40 — a subject's whole paper in
     *  one call, since a call returns a few fields per question. */
    classifyQuestionsBatchSize?: number;
    /** How many PDF pages the verbatim-extraction pass sends per LLM call.
     *  Defaults to 1. Extraction used to send the whole document in ONE call,
     *  whose JSON output overran the provider token cap on a dense paper and
     *  truncated to invalid JSON, failing the import (dev run 4471c523).
     *  Bounding the pages per call bounds the output. Setting this at or above
     *  the page cap reproduces the old single-call behaviour.
     *  See docs/FeatureDesigns/extraction-page-chunking-target.md. */
    extractionPageChunkSize?: number;
    /** How many extraction page-chunks run concurrently. Defaults to 3. Kept
     *  modest so a large paper doesn't monopolise provider quota. */
    extractionChunkConcurrency?: number;
    /** How many pages of an uploaded solution the page reader sends per LLM call.
     *  Defaults to 1. Cost should barely move with it (input is a small part of a
     *  call's cost); what moves is the output per call, so more pages risks
     *  hitting the output limit, and the slowest call finishes later. At the page
     *  cap the whole upload is one call, like the splitter.
     *  See docs/FeatureDesigns/solution-analysis/solution-page-reader-target.md §7. */
    solutionPageReadChunkSize?: number;
    /** How many of those page-reader calls run at once. Defaults to 8, so a
     *  school's 40-page script is read in five batches. */
    solutionPageReadConcurrency?: number;
    /** Default ceiling on estimated LLM spend for one operation run, in USD.
     *  Unset or 0 = no ceiling. Used for any operation without its own entry in
     *  `operationCostCapsUsd`.
     *
     *  Enforced BEFORE a call, not discovered after it: once a run's accrued
     *  cost reaches the ceiling, the next LLM call is refused. Prod extraction
     *  run 7792e1f3 made 24 calls for $1.4953 and produced nothing — the spend
     *  was only visible once it was over.
     *
     *  Applies per operation RUN (one import, one analysis), not per day or per
     *  tenant. See docs/FeatureDesigns/PipelineInfra/llm-metrics-observability.md. */
    operationCostCapUsd?: number;
    /** Per-operation ceilings, overriding `operationCostCapUsd`.
     *
     *  Operations differ by more than an order of magnitude — a PDF import runs
     *  an extraction plus N enrichment batches and legitimately costs over a
     *  dollar, while a single hint is fractions of a cent. One global number
     *  cannot be both a useful backstop for the first and any bound at all on
     *  the second. An explicit 0 here means "no ceiling for this operation",
     *  which is how you exempt one without raising the default for everything. */
    operationCostCapsUsd?: Partial<Record<OperationType, number>>;
    /** Which email provider to use for outbound mail (invitations, reports).
     *  Defaults to 'sendgrid' when unset for backwards compatibility. */
    emailProvider?: 'sendgrid' | 'resend';
    /** Which SMS provider to use for outbound texts (phone OTP). Defaults to
     *  'msg91' when unset. See docs/FeatureDesigns/phone-otp-login-target.md. */
    smsProvider?: 'msg91' | 'twilio';
    /** How a phone OTP reaches the person. `'sms'` (the default when unset) uses
     *  the MSG91/DLT path; `'whatsapp'` sends the Meta authentication template
     *  named by `whatsapp-settings.otpTemplate`. Settings-driven so the channel
     *  can be switched centrally the moment a template clears Meta review, with
     *  no redeploy. A WhatsApp send that fails falls back to SMS rather than
     *  stranding the person mid-login — see auth/otp.ts.
     *  See docs/FeatureDesigns/phone-otp-login-target.md. */
    phoneOtpTransport?: 'sms' | 'whatsapp';
    /** The sender shown to students and teachers on an admin's reply to their
     *  app feedback, in place of the replying admin's own name or login email.
     *  Applied when a non-admin READS the thread; the stored message keeps the
     *  real admin, which admins still see. Unset → DEFAULT_SUPPORT_EMAIL.
     *  See functions/src/threads/supportSender.ts. */
    supportEmail?: string;
    /** Resend API key. When unset, falls back to RESEND_API_KEY env var so
     *  existing deployments continue to work after this field was introduced. */
    resendApiKey?: string;
    /** Public base URLs for each front-end app. The dev `app-settings` doc holds
     *  dev URLs, the prod doc holds prod URLs — single source of truth for links
     *  generated server-side (e.g. the re-engagement deep link, invite-email
     *  fallbacks). HTTP flows still prefer the request Origin; these are the
     *  fallback when there is no request (cron) or the Origin is unrecognized.
     *  Unset → code falls back to the prod URLs. */
    appUrls?: {
        student?: string;
        teacher?: string;
        admin?: string;
    };
    // Rate limiting
    rateLimits?: RateLimitConfig;
    // Tracing settings
    tracing?: TracingSettings;
    webTracing?: WebTracingSettings;
    updatedAt: string;
    updatedBy?: string;
}

/** Shown as the sender of admin replies to user feedback when `AppSettings.supportEmail` is unset. */
export const DEFAULT_SUPPORT_EMAIL = 'support@buzzbrain.ai';
